import React from 'react'
import { Form } from 'react-bootstrap'
import { PlanDefinition } from '../types'

interface Props {
  plans: PlanDefinition[]
  selectedPlanCode: string | null
  onSelect: (planCode: string) => void
  compact?: boolean
  disabledPlanCodes?: string[]
}

const PlanSelector: React.FC<Props> = ({ plans, selectedPlanCode, onSelect, compact, disabledPlanCodes = [] }) => {
  if (plans.length === 0) {
    return <div className='alert alert-warning'>No plans available for your grade.</div>
  }

  // Single plan → auto-selected, no need to show selector
  if (plans.length === 1) {
    return null
  }

  // Compact mode — small inline pills
  if (compact) {
    return (
      <div className='d-flex flex-wrap gap-2'>
        {plans.map((plan) => {
          const isSelected = plan.planCode === selectedPlanCode
          const disabled = disabledPlanCodes.includes(plan.planCode)
          return (
            <span
              key={plan.planCode}
              className={`badge cursor-pointer px-3 py-2 fs-8 ${
                isSelected ? 'badge-primary' : 'badge-light-primary'
              }`}
              onClick={() => !disabled && onSelect(plan.planCode)}
              aria-disabled={disabled}
              title={disabled ? 'Remove incompatible family members before selecting this plan.' : undefined}
              role='button'
            >
              {plan.planName}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className='mb-6'>
      <label className='form-label fw-bold fs-6'>Select Plan</label>
      <div className='row g-3'>
        {plans.map((plan) => {
          const isSelected = plan.planCode === selectedPlanCode
          const disabled = disabledPlanCodes.includes(plan.planCode)
          return (
            <div className='col-md-6' key={plan.planCode}>
              <div
                className={`border rounded p-3 ${disabled ? 'opacity-50' : 'cursor-pointer'} ${
                  isSelected
                    ? 'border-primary bg-light-primary'
                    : 'border-gray-300 bg-white hover-elevate-up'
                }`}
                onClick={() => !disabled && onSelect(plan.planCode)}
                role='button'
                aria-disabled={disabled}
              >
                <div className='d-flex align-items-center'>
                  <Form.Check
                    type='radio'
                    name='plan-select'
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => onSelect(plan.planCode)}
                    className='me-3'
                  />
                  <div>
                    <div className='fw-bolder fs-6'>{plan.planName}</div>
                    {disabled && (
                      <div className='text-muted fs-7'>Remove incompatible family members before selecting this plan.</div>
                    )}
                    <div className='text-muted fs-7 mt-1'>
                      Max {plan.maxLives} members
                      {plan.groupingModel !== 'Combined' && (
                        <span className='badge badge-light-info ms-2'>{plan.groupingModel}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PlanSelector
